import { useState, useEffect, useCallback, useRef } from 'react';
import { getCSRFToken } from '../../utils/csrf';
import { route } from '../../utils/route';

type PushStatus = 'loading' | 'enabled' | 'disabled' | 'denied' | 'unsupported';

type MarketingChannel = 'email' | 'push' | 'whatsapp';

function Toggle({ on, disabled, onClick, label }: {
    on: boolean;
    disabled: boolean;
    onClick: () => void;
    label: string;
}) {
    return (
        <button
            className={`relative inline-flex h-7 w-12 shrink-0 rounded-full border-none transition-colors duration-200 ${
                on ? 'bg-green-600' : 'bg-gray-300'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
        >
            <span
                className={`pointer-events-none inline-block h-[22px] w-[22px] rounded-full bg-white shadow transition-transform duration-200 ${
                    on ? 'translate-x-5' : 'translate-x-[3px]'
                } mt-[3px]`}
            />
        </button>
    );
}

export function NotificationsSection() {
    const [pushStatus, setPushStatus] = useState<PushStatus>('loading');
    const [toggling, setToggling] = useState(false);
    const osRef = useRef<any>(null);

    const [channels, setChannels] = useState<Record<MarketingChannel, boolean>>({
        email: false,
        push: false,
        whatsapp: false,
    });
    const [loading, setLoading] = useState(true);
    const [togglingChannel, setTogglingChannel] = useState<MarketingChannel | null>(null);
    const [marketingError, setMarketingError] = useState<string | null>(null);
    const [pushError, setPushError] = useState<string | null>(null);

    const checkStatus = useCallback(() => {
        const OS = osRef.current;
        if (!OS) { setPushStatus('unsupported'); return; }
        if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
            setPushStatus('denied');
            return;
        }
        const sub = OS.User?.PushSubscription;
        setPushStatus(sub?.optedIn ? 'enabled' : 'disabled');
    }, []);

    useEffect(() => {
        const deferred = (window as any).OneSignalDeferred;
        if (!deferred) { setPushStatus('unsupported'); return; }

        let resolved = false;
        deferred.push(async (OS: any) => {
            resolved = true;
            osRef.current = OS;
            checkStatus();
            OS.User?.PushSubscription?.addEventListener('change', checkStatus);
        });

        const timeout = setTimeout(() => {
            if (!resolved) {
                console.warn('[OneSignal] deferred callback did not fire after 10s');
                setPushStatus('unsupported');
            }
        }, 10000);

        return () => {
            clearTimeout(timeout);
            osRef.current?.User?.PushSubscription?.removeEventListener('change', checkStatus);
        };
    }, [checkStatus]);

    useEffect(() => {
        fetch(route('api.profile.notifications'), {
            credentials: 'same-origin',
            headers: { 'Accept': 'application/json' },
        })
            .then(r => r.json())
            .then(data => {
                if (data.success && data.channels) {
                    setChannels(data.channels);
                }
            })
            .catch(() => setMarketingError('No se pudo cargar las preferencias.'))
            .finally(() => setLoading(false));
    }, []);

    const handleDevicePushToggle = useCallback(async () => {
        const OS = osRef.current;
        if (!OS || toggling) return;
        setPushError(null);
        setToggling(true);
        try {
            if (pushStatus === 'enabled') {
                await OS.User.PushSubscription.optOut();
            } else {
                await OS.Notifications.requestPermission();
                await OS.User.PushSubscription.optIn();
            }
            checkStatus();
        } catch (e: any) {
            setPushError(e?.message || String(e));
            checkStatus();
        } finally {
            setToggling(false);
        }
    }, [pushStatus, toggling, checkStatus]);

    const updateChannel = useCallback(async (channel: MarketingChannel, enabled: boolean) => {
        setTogglingChannel(channel);
        setMarketingError(null);

        try {
            const res = await fetch(route('api.profile.notifications.update'), {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-XSRF-TOKEN': getCSRFToken(),
                },
                body: JSON.stringify({ channel, enabled }),
            });
            const data = await res.json();
            if (data.success && data.channels) {
                setChannels(data.channels);
            } else {
                setMarketingError(data.message || 'Error al actualizar.');
            }
        } catch {
            setMarketingError('Error al actualizar.');
        } finally {
            setTogglingChannel(null);
        }
    }, []);

    const isOn = pushStatus === 'enabled';
    const isDenied = pushStatus === 'denied';
    const isLoading = pushStatus === 'loading';
    const isUnsupported = pushStatus === 'unsupported';

    return (
        <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Notificaciones</h2>

            <div className="space-y-0 divide-y divide-gray-100">
                <div className="flex items-center justify-between py-3">
                    <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium text-gray-900">Notificaciones push en este dispositivo</span>
                        <span className="text-xs text-gray-500">Permitirle al navegador mostrarte alertas.</span>
                    </div>
                    {isDenied ? (
                        <span className="text-xs text-gray-400 italic">Bloqueadas en el navegador</span>
                    ) : (
                        <Toggle
                            on={isOn}
                            disabled={isLoading || toggling || isUnsupported}
                            onClick={handleDevicePushToggle}
                            label={isOn ? 'Desactivar notificaciones' : 'Activar notificaciones'}
                        />
                    )}
                </div>

                {(isUnsupported || isDenied) && (
                    <p className="text-xs text-gray-400 pb-3 pt-1 border-none">
                        {isDenied
                            ? 'Para activarlas, cambiá los permisos de notificación en la configuración de tu navegador.'
                            : /iPhone|iPad|iPod/.test(navigator.userAgent)
                                ? 'Las notificaciones push en iOS requieren iOS 16.4+ y que la app esté instalada en tu pantalla de inicio.'
                                : 'Las notificaciones push no están disponibles en este navegador.'}
                    </p>
                )}

                {pushError && (
                    <p className="text-xs text-red-500 pb-3 pt-1 border-none">
                        Error: {pushError}
                    </p>
                )}

                <div className="flex items-center justify-between py-3">
                    <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium text-gray-900">Emails de marketing</span>
                        <span className="text-xs text-gray-500">Recibí ofertas, novedades y promociones por email.</span>
                    </div>
                    <Toggle
                        on={channels.email}
                        disabled={loading || togglingChannel === 'email'}
                        onClick={() => updateChannel('email', !channels.email)}
                        label={channels.email ? 'Desactivar emails de marketing' : 'Activar emails de marketing'}
                    />
                </div>

                <div className="flex items-center justify-between py-3">
                    <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium text-gray-900">Push de marketing</span>
                        <span className="text-xs text-gray-500">Ofertas y novedades por notificación push.</span>
                    </div>
                    <Toggle
                        on={channels.push}
                        disabled={loading || togglingChannel === 'push'}
                        onClick={() => updateChannel('push', !channels.push)}
                        label={channels.push ? 'Desactivar push de marketing' : 'Activar push de marketing'}
                    />
                </div>

                <div className="flex items-center justify-between py-3">
                    <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium text-gray-900">WhatsApp de marketing</span>
                        <span className="text-xs text-gray-500">Promociones y novedades por WhatsApp.</span>
                    </div>
                    <Toggle
                        on={channels.whatsapp}
                        disabled={loading || togglingChannel === 'whatsapp'}
                        onClick={() => updateChannel('whatsapp', !channels.whatsapp)}
                        label={channels.whatsapp ? 'Desactivar WhatsApp de marketing' : 'Activar WhatsApp de marketing'}
                    />
                </div>
            </div>

            {marketingError && (
                <p className="text-xs text-red-500 mt-1">{marketingError}</p>
            )}
        </div>
    );
}
